import React, { useEffect, useState } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { aboutContent, siteInfo } from '../data/mock';
import { MapPin, Award, Heart, Leaf } from 'lucide-react';

const AboutPage = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
  }, []);

  const values = [
    {
      icon: Heart,
      title: "Passion",
      description: "Chaque création est le fruit d'une passion profonde pour l'art floral et la beauté naturelle."
    },
    {
      icon: Leaf,
      title: "Nature",
      description: "Nous respectons la nature en utilisant des fleurs fraîches et des techniques éco-responsables."
    },
    {
      icon: Award,
      title: "Excellence",
      description: "Nous nous engageons à délivrer une qualité exceptionnelle dans chaque composition."
    }
  ];

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 lg:pt-40 lg:pb-24 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 right-20 w-64 h-64 bg-[#E8A5C4]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-20 w-80 h-80 bg-[#C45B9F]/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div
            className={`text-center max-w-3xl mx-auto transition-all duration-1000 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
          >
            <span className="text-[#C45B9F] font-medium uppercase tracking-wider text-sm">
              Notre histoire
            </span>
            <h1 className="font-heading text-5xl lg:text-6xl text-[#3D2E35] mt-4">
              À propos de <span className="font-script text-[#8B4B7C]">Tevaite</span>
            </h1>
            <p className="text-lg text-[#6B5A62] mt-6 leading-relaxed">
              {aboutContent.subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <div
              className={`relative transition-all duration-1000 delay-200 ${
                isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
              }`}
            >
              <div className="relative">
                <div className="overflow-hidden rounded-[2rem]">
                  <img
                    src={aboutContent.image}
                    alt="Tevaite Création"
                    className="w-full h-[500px] object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 -right-6 w-full h-full border-2 border-[#C45B9F] rounded-[2rem] -z-10" />
              </div>
            </div>

            {/* Content */}
            <div
              className={`space-y-6 transition-all duration-1000 delay-400 ${
                isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
              }`}
            >
              <h2 className="font-heading text-3xl lg:text-4xl text-[#3D2E35]">
                Une passion née au cœur du Pacifique
              </h2>
              {aboutContent.paragraphs.map((paragraph, index) => (
                <p key={index} className="text-[#6B5A62] leading-relaxed">
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 lg:py-24 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#C45B9F] font-medium uppercase tracking-wider text-sm">
              Nos valeurs
            </span>
            <h2 className="font-heading text-4xl lg:text-5xl text-[#3D2E35] mt-4">
              Ce qui nous anime
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className={`card-tevaite p-8 text-center transition-all duration-700 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${(index + 3) * 150}ms` }}
              >
                <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-[#E8A5C4] to-[#C45B9F] flex items-center justify-center mb-6">
                  <value.icon className="text-white" size={28} />
                </div>
                <h3 className="font-heading text-xl text-[#3D2E35] mb-3">
                  {value.title}
                </h3>
                <p className="text-[#6B5A62]">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Locations Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#C45B9F] font-medium uppercase tracking-wider text-sm">
              Notre présence
            </span>
            <h2 className="font-heading text-4xl lg:text-5xl text-[#3D2E35] mt-4">
              Une dimension internationale
            </h2>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            {siteInfo.locations.map((location, index) => (
              <div
                key={index}
                className={`flex items-center space-x-3 px-6 py-4 rounded-2xl bg-[#FAF8F5] border border-[#E8A5C4]/20 transition-all duration-500 hover:shadow-lg hover:border-[#C45B9F]/30 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${(index + 6) * 100}ms` }}
              >
                <span className="text-2xl">{location.flag}</span>
                <span className="text-[#3D2E35] font-medium">{location.name}</span>
                <MapPin size={16} className="text-[#C45B9F]" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
